import { Box, Grid, Heading, Text, Flex, useDisclosure, Divider, Link, Accordion, SimpleGrid } from '@chakra-ui/react';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import LogoLoader from 'components/Loader/LogoLoader';
import { PlanCard, PriceCalculator, PlanAddOn } from 'containers/AccountBilling/shared';
import ControlSwitch from 'containers/AccountBilling/shared/ControlSwitch';
import { PLANS } from 'global/plans';
import { useFetchBillingDetails } from 'hooks/accountBilling/useFetchBillingDetails';
import { useFetchPlans } from 'hooks/accountBilling/useFetchPlans';
import { useCompanyInfo } from 'store/useUserStore';

import { addOnDetails, billingPlans } from '../shared/billingData';

export interface ISelectedPlanAddOn {
  id: string;
  name: string;
  price: number;
}

const UpgradeBilling: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'accountBilling']);

  const myRef = useRef<any>(null);

  const companyInfo: any = useCompanyInfo();

  const [selectedPlan, setSelectedPlan] = useState<any>(companyInfo?.plan ?? PLANS.freeTrial);
  const [hasChangedPlan, setHasChangedPlan] = useState<boolean>(false);
  const [total, setTotal] = useState<any>(0);
  const [selectedAddOns, setSelectedAddOns] = useState<ISelectedPlanAddOn[]>([]);
  const [addOnsChanged, setAddOnsChanged] = useState<boolean>(false);

  const { isOpen: isYearly, onToggle } = useDisclosure({ defaultIsOpen: true });
  const getPlanDuration = () => (isYearly ? 'annual' : 'monthly');
  const scrollToPriceCalculator = () => myRef.current?.scrollIntoView();

  const { data: billingDetailsData, status: billingDetailsStatus } = useFetchBillingDetails();
  const { data, status } = useFetchPlans(getPlanDuration());

  const planAddOns = addOnDetails(isYearly, t);

  const updatePlan = (name: string, price: number) => {
    if (name.toLowerCase() === PLANS.basic || name.toLowerCase() === PLANS.freeTrial) {
      setSelectedAddOns([]);
    }
    scrollToPriceCalculator();
    setSelectedPlan(name);
    setTotal(price);
  };

  const updateAddOns = (e: React.ChangeEvent<HTMLInputElement>, addOn: ISelectedPlanAddOn): void => {
    if (e.target.checked) {
      const updatedSelectedAddOns = [...selectedAddOns, { ...addOn }];
      setSelectedAddOns(updatedSelectedAddOns);
    } else {
      const updatedSelectedAddOns = selectedAddOns.filter(item => item.id !== addOn.id);
      setSelectedAddOns(updatedSelectedAddOns);
    }
  };

  useEffect(() => {
    if (billingDetailsStatus === 'success') {
      if (billingDetailsData.payload['add-ons']) {
        setAddOnsChanged(
          selectedAddOns.every(addOn => (billingDetailsData.payload['add-on'] as string[])?.includes(addOn.id))
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddOns]);

  const newBillingPlans = billingPlans(
    isYearly,
    billingDetailsData?.payload?.duration_type,
    selectedPlan?.toLowerCase(),
    updatePlan,
    () => setHasChangedPlan(true)
  );

  const toggleDuration = () => {
    onToggle();
  };

  useEffect(() => {
    if (billingDetailsStatus === 'success') {
      const planDuration: string = (billingDetailsData.payload.duration_type as string).toLowerCase() || 'annual';
      if (planDuration === 'monthly') {
        // Toggle the plans to monthly
        onToggle();
      }

      if ((billingDetailsData.payload['add-ons'] as string[])?.length > 0) {
        const addOns: string[] = billingDetailsData.payload['add-ons'] as string[];
        const filteredAddOns: ISelectedPlanAddOn[] = planAddOns
          .filter(addOn => addOns.includes(addOn.id))
          .map(addOn => ({
            id: addOn.id,
            name: addOn.heading,
            price: addOn.price[isYearly ? 'annually' : 'monthly'] as number,
          }));
        setSelectedAddOns(filteredAddOns);
      } else {
        setSelectedAddOns([]);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingDetailsStatus]);

  useEffect(() => {
    if (billingDetailsStatus === 'success') {
      if ((billingDetailsData.payload['add-ons'] as string[])?.length > 0) {
        const addOns: string[] = billingDetailsData.payload['add-ons'] as string[];
        const filteredAddOns: ISelectedPlanAddOn[] = planAddOns
          .filter(addOn => addOns.includes(addOn.id))
          .map(addOn => ({
            id: addOn.id,
            name: addOn.heading,
            price: addOn.price[isYearly ? 'annually' : 'monthly'] as number,
          }));
        setSelectedAddOns(filteredAddOns);
      } else {
        setSelectedAddOns([]);
      }

      updatePlan(
        selectedPlan,
        newBillingPlans.filter(plan => plan.heading.toLowerCase() === selectedPlan.toLowerCase())[0]?.price as number
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isYearly]);

  if (status === 'loading' || billingDetailsStatus === 'loading') return <LogoLoader />;

  return (
    <>
      <Box p={{ sm: 2, md: 4 }} pt={0}>
        <Flex textAlign="center" justifyContent="center">
          <Heading
            as="h2"
            color="gray.900"
            fontSize={['lg', 'xl', '3xl']}
            py={2}
            w={['100%', '50%', '40%']}
            fontWeight="600"
          >
            {t('accountBilling:chooseAPlanThatworksBestForYouAndYourTeam')}
          </Heading>
        </Flex>
        <Box pt={6} m="auto" display="flex" alignItems="center" justifyContent="center">
          <ControlSwitch isChecked={isYearly} handleChange={toggleDuration} />
        </Box>

        <Box py={10}>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(3,1fr)' }} gap={6} mb={12}>
            {newBillingPlans.map((data, index) => (
              <PlanCard {...data} key={index} />
            ))}
          </Grid>

          <Flex justifyContent="space-between" alignItems="center">
            <Heading as="h3" fontSize="2xl" fontWeight="600" color="newGray.1000" mb={6}>
              {t('accountBilling:allPlansFeatures')}
            </Heading>
            <Link href="https://timeero.com/pricing" target="_blank" rel="noreferrer">
              <Flex alignItems="center">
                <Text color="primary.500" fontSize="md">
                  {t('common:seeAll')}
                </Text>
              </Flex>
            </Link>
          </Flex>
          <Divider borderStyle="dashed" borderColor="newGray.500" mb={6} />
          {/* <Box position="relative">
            <Flex justifyContent="space-between" alignItems="center" position="sticky" top="0" bg="white" zIndex={1}>
              <Heading
                as="h4"
                fontSize="xl"
                fontWeight="600"
                color="newGray.1000"
                mb="4"
                display={['none', null, 'block']}
                w="40%"
              >
                {t('accountBilling:features')}
              </Heading>
              <Grid
                templateColumns="repeat(3, 1fr)"
                gap={8}
                color="newGray.1000"
                fontSize="md"
                w="full"
                mb={6}
                alignItems="center"
              >
                {newBillingPlans.map((data, index) => (
                  <GridItem w="100%" h="100%" textAlign="center" key={index}>
                    <Text fontSize="lg" fontWeight="600" mb={1}>
                      {data.heading}
                      <br />
                      <Text as="span" fontSize="sm" fontWeight="300" color="gray.500">
                        ${data.price}
                        {t('accountBilling:slashPerUser')},{' '}
                        {(isYearly
                          ? t('accountBilling:billedAnnually')
                          : t('accountBilling:billedMonthly')
                        ).toLowerCase()}
                      </Text>
                    </Text>
                    <Button
                      mt={4}
                      colorScheme="secondary"
                      onClick={data.onBtnClick}
                      isDisabled={data.isCurrentPlan}
                      variant={data.isSelected ? 'outline' : 'solid'}
                    >
                      {isLargerThan768
                        ? data.isCurrentPlan
                          ? `You are on ${data.heading}`
                          : `Switch to ${data.heading}`
                        : data.isCurrentPlan
                        ? 'Current'
                        : `Switch`}
                    </Button>
                  </GridItem>
                ))}
              </Grid>
            </Flex>

            <Collapse startingHeight={475} in={isFeaturesOpen}>
              {billingFeatures(t).map((features: IBillingFeature, index: number) => (
                <FeatureRow {...features} key={index} />
              ))}
            </Collapse>
          </Box>
          <Flex justifyContent="center">
            <Button onClick={toggleFeaturesOpen} variant="outline">
              {isFeaturesOpen ? 'Show Less' : 'Show More'}
            </Button>
          </Flex> */}
          {['pro', 'premium'].includes(selectedPlan.toLowerCase()) ? (
            <>
              <Heading as="h3" fontSize="2xl" fontWeight="600" color="newGray.1000" my={6}>
                {t('accountBilling:optionalAddOns')}
              </Heading>
              <Divider borderStyle="dashed" borderColor="newGray.500" mb={6} />
              <Accordion allowMultiple>
                <SimpleGrid columns={3} spacing={3} minChildWidth="300px">
                  {planAddOns.map((addOn, index: number) => (
                    <PlanAddOn
                      key={index}
                      {...addOn}
                      onChecked={e =>
                        updateAddOns(e, {
                          id: addOn.id,
                          name: addOn.heading,
                          price: addOn.price[isYearly ? 'annually' : 'monthly'] as number,
                        })
                      }
                      isChecked={selectedAddOns.some(item => item.id === addOn.id)}
                    />
                  ))}
                </SimpleGrid>
              </Accordion>
            </>
          ) : null}
          {hasChangedPlan && (
            <>
              <Heading as="h3" fontSize="2xl" fontWeight="600" color="newGray.1000" my={6}>
                {t('accountBilling:checkYourPrice')}
              </Heading>
              <Divider borderStyle="dashed" borderColor="newGray.500" mb={6} />
              <Box ref={myRef}>
                <PriceCalculator
                  selectedPlan={selectedPlan}
                  noOfEmployees={data?.payload[0]?.total_users}
                  costPerUser={total ? total : 0}
                  addOns={selectedAddOns}
                  isYearly={isYearly}
                  addOnsChanged={addOnsChanged}
                  isAlreadyCustomer={companyInfo?.plan?.toLowerCase() !== PLANS.freeTrial}
                  isCurrentPlan={
                    companyInfo?.plan?.toLowerCase() === selectedPlan?.toLowerCase() &&
                    ((billingDetailsData.payload.duration_type === 'annual' && isYearly) ||
                      (billingDetailsData.payload.duration_type === 'monthly' && !isYearly))
                  }
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default UpgradeBilling;
